import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import {Session} from 'nutshell-core/session';

import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {ListView} from 'shells/list-view/list-view';

import type {McfxFormFragment as McfxForm} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import type {NutshellState} from '../../../store';

import {useGetAnalyticsStatus} from '../../email-marketing/analytics/use-get-analytics-status';
import {fetchSchemaAction} from '../../forms/forms-actions';
import {getFormSchema} from '../../forms/forms-selectors';

import {useIgnoreForm} from '../connected-forms-page/use-ignore-form';
import {useUnignoreForm} from '../connected-forms-page/use-unignore-form';

import {ConnectedFormsListItem} from './connected-forms-list-item';
import {getNumSubmissions} from './helpers';

import styles from './connected-forms-list-page.module.css';

type Props = {
    isLoading: boolean;
    error: string | null | undefined;
    mcfxForms: McfxForm[];
    isViewingIgnored: boolean;
    isFiltering: boolean;
    refetch: () => Promise<any>;
};

export function ConnectedFormsList(props: Props) {
    const dispatch = useDispatch();
    const formSchema = useSelector((state: NutshellState) => getFormSchema(state));
    // @ts-expect-error
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const canAccessSetup = loggedInUser && loggedInUser.permissions.canAccessSetup;

    React.useEffect(() => {
        // @ts-expect-error
        dispatch(fetchSchemaAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ignoreForm = useIgnoreForm();
    const unignoreForm = useUnignoreForm();
    const {analyticsStatus} = useGetAnalyticsStatus();

    return (
        <ListView
            collection={props.mcfxForms}
            itemSpacing={16}
            renderRow={(mcfxForm) => {
                return (
                    <div key={mcfxForm.id} className={styles['list-item']}>
                        <ConnectedFormsListItem
                            name={
                                mcfxForm.wfForm && mcfxForm.wfForm.name.length
                                    ? mcfxForm.wfForm.name
                                    : 'Form'
                            }
                            htmlUrlPath={mcfxForm.wfForm ? mcfxForm.wfForm.htmlUrlPath : '#'}
                            lastSubmittedTime={
                                mcfxForm.lastSubmission
                                    ? mcfxForm.lastSubmission.createdTime
                                    : undefined
                            }
                            numSubmissions={getNumSubmissions(mcfxForm)}
                            status={mcfxForm.wfForm ? mcfxForm.wfForm.status : 'NOT_ACTIVATED'}
                            fields={
                                mcfxForm.wfForm && mcfxForm.wfForm.publishedVersion
                                    ? mcfxForm.wfForm.publishedVersion.fields
                                    : []
                            }
                            formSchema={formSchema}
                            ignoreForm={() => {
                                if (mcfxForm.wfForm) {
                                    return ignoreForm({wfFormId: mcfxForm.wfForm.id}).then(
                                        props.refetch
                                    );
                                } else {
                                    return Promise.reject();
                                }
                            }}
                            unignoreForm={() => {
                                if (mcfxForm.wfForm) {
                                    return unignoreForm({wfFormId: mcfxForm.wfForm.id}).then(
                                        props.refetch
                                    );
                                } else {
                                    return Promise.reject();
                                }
                            }}
                        />
                    </div>
                );
            }}
            isLoading={props.isLoading}
            loadingState={{itemHeight: 150, backgroundColor: 'white', listPadding: 0}}
            isError={Boolean(props.error)}
            errorState={{errorMessage: props.error}}
            isFiltering={props.isFiltering || props.isViewingIgnored}
            nothingFoundState={{
                title: props.isViewingIgnored ? 'No forms are ignored' : 'No forms found',
                subtitle: props.isViewingIgnored
                    ? 'Ignore a form to hide it from your main list and show it only when this filter is selected'
                    : undefined,
            }}
            emptyStateComponent={
                <EmptyStateLarge
                    icon={
                        <div className={styles['empty-state-image']}>
                            <Illustration src='site-visitors' />
                        </div>
                    }
                    title='Collect leads from any form on your website'
                    subtitle='Set up Nutshell Analytics to capture submissions from forms created with other tools'
                    detailTextBullets={[
                        <>
                            Set up tracking by embedding a small snippet of code on your website (if
                            it’s built with WordPress, you can try our{' '}
                            <Link
                                variant='primary'
                                href='https://wordpress.org/plugins/nutshell-analytics/#description'
                                newTab={true}
                                size='big'
                            >
                                plugin
                            </Link>
                            )
                        </>,
                        'Wait for submissions to the forms on your site that weren’t created with Nutshell – they’ll appear here when detected',
                        'Decide which fields will save submission data to people, companies, and leads',
                    ]}
                    secondaryCta={
                        analyticsStatus &&
                        analyticsStatus.status !== 'CONFIGURED' &&
                        canAccessSetup && (
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                to='/setup/tracking-snippet'
                                variant='button-primary'
                                size='xlarge'
                            >
                                Set up tracking
                            </Link>
                        )
                    }
                    cta={
                        <Link
                            variant='button-secondary'
                            onClick={() => {
                                analyticsClient.track(
                                    analyticsClient.EVENTS.TRACK_CONNECTED_FORM_LEARN_MORE_CLICKED
                                );
                            }}
                            href='https://support.nutshell.com/en/articles/8429026-nutshell-analytics-tracking-website-visitors'
                            newTab={true}
                            size='xlarge'
                        >
                            Learn more
                        </Link>
                    }
                />
            }
        />
    );
}
